import React, { type ReactElement } from 'react';

import Button from '@atlaskit/button/standard-button';
import Modal, {
	ModalBody,
	ModalFooter,
	ModalHeader,
	ModalTitle,
	ModalTransition,
} from '@atlaskit/modal-dialog';
import { ShortcutScope } from '@atlassian/jira-common-components-keyboard-shortcuts';
import { useIntl } from '@atlassian/jira-intl';

import { PRE_OAUTH_MODAL_TEST_ID } from '../../../../common/constants';
import type { SupportedThirdParty } from '../../../../common/constants/import-type';
import { useContentForThirdParty, useUrlForThirdParty } from '../../../../common/utils';
import messages from './messages';

type Props = {
	isOpen: boolean;
	thirdParty: SupportedThirdParty;
	onClose: () => void;
	onNext: () => void;
};

const PreOAuthModal = ({ isOpen, thirdParty, onClose, onNext }: Props) => {
	const { formatMessage } = useIntl();
	const { preOAuthModalTitle, preOAuthModalDescription } = useContentForThirdParty(thirdParty);
	const { appDownloadUrl } = useUrlForThirdParty(thirdParty);

	return (
		<ModalTransition>
			{isOpen && (
				<ShortcutScope>
					<Modal onClose={onClose} testId={PRE_OAUTH_MODAL_TEST_ID}>
						<ModalHeader>
							<ModalTitle>{preOAuthModalTitle}</ModalTitle>
						</ModalHeader>

						<ModalBody>
							{preOAuthModalDescription && (
								<p>
									{formatMessage(preOAuthModalDescription, {
										br: <br />,
										link: (children): ReactElement => (
											<a href={appDownloadUrl} target="_blank">
												{children}
											</a>
										),
									})}
								</p>
							)}
						</ModalBody>

						<ModalFooter>
							<Button appearance="subtle" onClick={onClose}>
								{formatMessage(messages.cancelButton)}
							</Button>
							<Button appearance="primary" onClick={onNext}>
								{formatMessage(messages.nextButton)}
							</Button>
						</ModalFooter>
					</Modal>
				</ShortcutScope>
			)}
		</ModalTransition>
	);
};

export default PreOAuthModal;
